import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import ProductCard from "@/components/ProductCard";
import Waveform from "@/components/Waveform";
import OutfitSets from "@/components/OutfitSets";

const HERO = "https://images.unsplash.com/photo-1744868646521-2620c945a1fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";
const CATS = [
  { key: "pulse", label: "PULSE", tagline: "Performance", img: "/products/tempo-tee-black-1.png", copy: "Built for the run, the lift, the sport." },
  { key: "core", label: "CORE", tagline: "Casual", img: "/products/core-hoodie-black-1.png", copy: "Everyday essentials with premium hand-feel." },
  { key: "accessories", label: "Accessories", tagline: "Gear", img: "/products/pulse-hat-black-1.png", copy: "The gear that moves with you." },
];
const IG = [
  "https://images.unsplash.com/photo-1727094141271-9bea5bc8c757?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
  "https://images.unsplash.com/photo-1733517301178-312a6f694f2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
  "https://images.unsplash.com/photo-1597892657493-6847b9640bac?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
  "https://images.unsplash.com/photo-1554139844-af2fc8ad3a3a?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
];
const CHAPTERS = [
  ["01", "In sync with your body", "Every seam, every fabric, every cut is engineered to move exactly as you do — nothing fighting you, nothing holding you back."],
  ["02", "In sync with the beat", "Music is the metronome of movement. SYNC is built for the tempo of your run, your lift, your walk through the city."],
  ["03", "In sync with your life", "Marathon mornings or a flight to somewhere new — SYNC carries the same energy wherever the day takes you."],
];

const line = { hidden: { y: "110%" }, show: (i) => ({ y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.15 } }) };

function Reveal({ children, className = "", delay = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7, delay }} className={className}>
      {children}
    </motion.div>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [drop, setDrop] = useState(null);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fade = useTransform(scrollYProgress, [0, 1], [1, 0]);

  useEffect(() => {
    api.get("/products?best_seller=true").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/drops").then((r) => setDrop(r.data.find((d) => d.status === "upcoming") || null)).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're in. Welcome to the movement.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally { setSubscribing(false); }
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src={HERO} alt="SYNC runner in motion" className="w-full h-[120%] object-cover grayscale" />
          <div className="absolute inset-0 bg-ink/75" />
        </motion.div>
        <motion.div style={{ opacity: fade }} className="relative container-sync pb-24 pt-32 w-full">
          <p className="eyebrow mb-6 text-chalk/70">Fitness · Running · Music · Streetwear</p>
          <h1 className="font-anton uppercase leading-[0.82] text-[18vw] sm:text-[15vw] lg:text-[11vw] text-chalk">
            {["Move With", "The Beat"].map((l, i) => (
              <span key={l} className="block overflow-hidden">
                <motion.span variants={line} custom={i} initial="hidden" animate="show" className="block">
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>
          <div className="flex flex-col sm:flex-row gap-4 mt-10">
            <Link to="/shop" className="btn-pulse" data-testid="hero-cta">Shop the Collection <ArrowRight size={16} /></Link>
            <Link to="/story" className="btn-outline" data-testid="hero-story">Our Story</Link>
          </div>
        </motion.div>
      </section>

      {/* BRAND STATEMENT */}
      <section className="bg-chalk text-ink py-28 sm:py-36" data-testid="brand-statement">
        <div className="container-sync">
          <Reveal>
            <p className="eyebrow !text-graphite mb-8">Stay in sync</p>
            <h2 className="font-anton uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-5xl">
              SYNC is more than activewear. It's built for the rhythm of your life —
              your heartbeat, your workout, your walk to somewhere new.
            </h2>
          </Reveal>
          <Waveform color="rgb(var(--ink))" className="mt-16" />
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-ink py-10 border-y border-border/60" data-testid="marquee">
        <Marquee speed={60} gradient={false}>
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="font-anton uppercase text-6xl sm:text-8xl marquee-stroke mx-8">
              Move With The Beat •
            </span>
          ))}
        </Marquee>
      </div>

      {/* BEST SELLERS */}
      <section className="bg-ink py-28 sm:py-32" data-testid="best-sellers">
        <div className="container-sync">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
            <div>
              <p className="eyebrow mb-4">Most popular</p>
              <h2 className="font-anton uppercase text-4xl sm:text-6xl leading-none">Best sellers</h2>
              <p className="mt-4 text-graphite max-w-xl">The pieces our community reaches for again and again — the drops that keep selling out.</p>
            </div>
            <Link to="/shop" className="font-mono text-xs uppercase tracking-[0.2em] text-chalk/80 hover:text-pulse link-underline">
              Shop all →
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* OUTFIT SETS */}
      <OutfitSets />

      {/* DROP TEASER */}
      {drop && (
        <section className="bg-ink pb-28" data-testid="drop-teaser">
          <div className="container-sync">
            <Reveal>
              <Link to="/drops" className="group relative block border border-signal/30 hover:border-signal transition-colors duration-300 p-10 sm:p-16 overflow-hidden"
                data-testid="drop-teaser-link">
                <p className="eyebrow mb-6 !text-signal">Limited drop — {drop.eta}</p>
                <h2 className="font-anton uppercase text-6xl sm:text-8xl leading-[0.85]">{drop.name}</h2>
                {drop.tagline && <p className="text-graphite mt-5 max-w-xl">{drop.tagline}</p>}
                <span className="inline-flex items-center gap-3 mt-9 font-mono text-xs uppercase tracking-[0.2em] text-chalk group-hover:text-signal transition-colors">
                  Join the waitlist — instant 10% off <ArrowRight size={16} />
                </span>
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      {/* CATEGORIES */}
      <section className="bg-ink pb-28" data-testid="categories">
        <div className="container-sync">
          <p className="eyebrow mb-10">Two collections. One movement.</p>
          <div className="grid gap-6 md:grid-cols-3">
            {CATS.map((c, i) => (
              <Reveal key={c.key} delay={i * 0.1}>
                <Link to={`/shop?collection=${c.key}`} className="group relative block overflow-hidden aspect-square bg-white"
                  data-testid={`category-${c.key}`}>
                  <img src={c.img} alt={c.label} className="w-full h-full object-contain p-4 transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
                  <div className="absolute bottom-0 p-7">
                    <p className="font-mono text-[10px] text-pulse uppercase tracking-[0.3em] mb-2">{c.tagline}</p>
                    <h3 className="font-anton uppercase text-4xl sm:text-5xl">{c.label}</h3>
                    <p className="font-mono text-xs text-chalk/70 mt-2 uppercase tracking-wider">{c.copy}</p>
                  </div>
                  <span className="absolute top-6 right-6 w-11 h-11 flex items-center justify-center bg-pulse text-ink opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ArrowRight size={18} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO / ABOUT */}
      <section className="bg-chalk text-ink py-28 sm:py-36" data-testid="about">
        <div className="container-sync grid lg:grid-cols-[0.9fr_1.1fr] gap-16 items-start">
          <div className="lg:sticky lg:top-28">
            <p className="eyebrow !text-graphite mb-6">The meaning behind SYNC</p>
            <h2 className="font-anton uppercase text-5xl sm:text-7xl leading-[0.9]">Movement isn't just exercise. It's a mindset.</h2>
            <img src="https://images.unsplash.com/photo-1597892657493-6847b9640bac?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="running detail" className="mt-10 w-full aspect-[4/3] object-cover grayscale" />
          </div>
          <div className="divide-y divide-line">
            {CHAPTERS.map(([num, title, body], i) => (
              <Reveal key={num} delay={i * 0.08}>
                <div className="py-10 first:pt-0">
                  <span className="font-mono text-sm text-pulse tracking-[0.3em]">{num}</span>
                  <h3 className="font-anton uppercase text-3xl sm:text-4xl mt-3">{title}</h3>
                  <p className="text-graphite text-lg leading-[1.85] mt-4 max-w-xl">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="bg-ink pb-28" data-testid="instagram">
        <div className="container-sync">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
            <h2 className="font-anton uppercase text-4xl sm:text-5xl">@sync.move</h2>
            <a href="#!" className="font-mono text-xs uppercase tracking-[0.2em] text-chalk/70 hover:text-signal link-underline">Follow the movement →</a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {IG.map((src, i) => (
              <a key={src} href="#!" className="group relative block overflow-hidden aspect-square" data-testid={`ig-${i}`}>
                <img src={src} alt="instagram" className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-signal/0 group-hover:bg-signal/20 transition-colors duration-300" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="bg-pulse text-ink py-28" data-testid="newsletter">
        <div className="container-sync text-center max-w-3xl mx-auto">
          <p className="font-mono text-xs uppercase tracking-[0.3em] mb-6">Join the movement</p>
          <h2 className="font-anton uppercase text-5xl sm:text-7xl leading-[0.9]">Get early access to new drops</h2>
          <p className="mt-6 text-ink/80 text-lg">Limited releases and the moments before everyone else sees them.</p>
          <form onSubmit={subscribe} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="newsletter-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com" data-testid="newsletter-email"
              className="flex-1 bg-transparent border border-ink/40 px-5 py-4 font-mono text-sm text-ink placeholder:text-ink/50 focus:border-ink outline-none" />
            <button type="submit" disabled={subscribing} data-testid="newsletter-submit"
              className="bg-ink text-chalk font-mono uppercase tracking-[0.15em] text-sm px-8 py-4 hover:bg-ink/85 transition-colors disabled:opacity-60">
              {subscribing ? "…" : "Join SYNC"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
